/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IView, IViewInfo } from "../../../view/IView";
import { IEditor } from "../../../IEditor";
import { IntegrationService } from "../../../integration/IntegrationService";
import { createEventEmitter, emitEvent, onEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { IEditorApiIntegrationResponse } from "@adapptio/lib-core";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IntegrationModel } from "../Common/IntegrationModel";
import { generateId } from "@adapptio/lib-components/src/Functions/generateId";
import { ResourceService } from "../../../resources/ResourceService";
import { TEditorBlueprintResource } from "../../../resources/ResourceTypes";
import { CommandsService } from "../../../commands/CommandsService";
import { ViewService } from "../../../view/ViewService";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { IntegrationEditorCreateLayoutConfig } from "./Layout";
import { IIntegrationType } from "../../../integration/IntegrationServiceTypes";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { ErrorTracker } from "@adapptio/lib-components";

/**
 * View parameters
 */
interface IIntegrationEditorCreateParams {
	basePath?: string;
}

/**
 * Error Log View
 */
export class IntegrationEditorCreate implements IView<IIntegrationEditorCreateParams> {
	/** Runtime service */
	private runtimeSvc: RuntimeService;

	/** Resource service */
	private resourceSvc: ResourceService;

	/** Integration  */
	private integrationSvc: IntegrationService;
	private integrationModel: IntegrationModel;
	private integrationId = generateId();
	public onIntegrationTypesChanged = createEventEmitter<void>();

	/** Save */
	private saveInProgress = false;
	public onSaveStateChange = createEventEmitter<void>();

	/** Commands Service */
	private commandsSvc: CommandsService;

	/** View */
	private viewSvc: ViewService;
	private viewId: string;

	/** Layout */
	private layout = new LayoutState(IntegrationEditorCreateLayoutConfig);

	private toastSvc: ToastService;

	private initBasePath?: string;

	/**
	 * View constructor
	 *
	 * @param dc Dependency container
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public constructor(private dc: IEditor) {
		this.commandsSvc = dc.get("commandsService");
		this.integrationSvc = dc.get("integrationService");
		this.runtimeSvc = dc.get("runtimeService");
		this.resourceSvc = dc.get("resourceService");
		this.viewSvc = dc.get("viewService");
		this.toastSvc = dc.get("toastService");

		this.integrationModel = new IntegrationModel(this.integrationSvc);
	}

	/**
	 * Initializes view
	 * @param params Params
	 */
	public async init(params: IIntegrationEditorCreateParams, id: string): Promise<void> {
		onEvent(this.integrationSvc.onIntegrationTypesChange, this.handleIntegrationTypesChanged);
		this.viewId = id;
		this.initBasePath = params.basePath;
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		removeAllEventListeners(this.onIntegrationTypesChanged);
		removeAllEventListeners(this.onSaveStateChange);

		this.layout.dispose();
	}

	public getBasePath(): string {
		return this.initBasePath;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo = (): IViewInfo => {
		return {
			label: "Integration",
			icon: "mdi/connection",
			canClose: true,
			isModified: false,
			renderWhenNotActive: true
		};
	};

	private handleIntegrationTypesChanged = () => {
		emitEvent(this.onIntegrationTypesChanged);
	};

	public getIntegrationTypes = (): IIntegrationType[] => {
		return this.integrationSvc.getIntegrationTypes();
	};

	private setSaveInProgress(saveInProgress: boolean) {
		this.saveInProgress = saveInProgress;
		emitEvent(this.onSaveStateChange);
	}

	public isSaving(): boolean {
		return this.saveInProgress;
	}

	private async createBlueprint(basePath: string, name: string): Promise<TEditorBlueprintResource> {
		const content = this.integrationModel.exportDocumentAsYAML();
		return await this.resourceSvc.createBlueprint(basePath, name, content, this.integrationId);
	}

	public createIntegration = async (
		integrationType: IEditorApiIntegrationResponse,
		name: string,
		basePath: string
	): Promise<void> => {
		if (this.saveInProgress) {
			return;
		}

		this.setSaveInProgress(true);

		this.integrationModel.createDefault(integrationType.integrationType);

		const idModel = this.integrationModel.getModel().props.id;
		idModel.schema.setValue(idModel, this.integrationId, false);

		try {
			const resource = await this.createBlueprint(basePath, name);
			this.commandsSvc.execute("resource.open", resource.id);
			this.viewSvc.close(this.viewId);
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				message: err?.detail || `Can't create blueprint ${name}`,
				details: String(err)
			});

			throw err;
		} finally {
			this.setSaveInProgress(false);
		}
	};
}
